import { useEffect, useState } from "react";
import type {
  PublicMapArea,
  PublicMapResponse,
  PublicPermitProduct,
} from "@natural/public-contract";
import {
  accountApi,
  type OrganizationDetail,
  type OrganizationTab,
} from "../../account";
import { resolveOrganizationTab } from "../model";

export function useOrganizationWorkspace(slug: string) {
  const [tab, setTabState] = useState<OrganizationTab>(() =>
    resolveOrganizationTab(new URLSearchParams(window.location.search).get("tab")),
  );
  const [data, setData] = useState<OrganizationDetail | null>(null);
  const [areas, setAreas] = useState<PublicMapArea[]>([]);
  const [products, setProducts] = useState<PublicPermitProduct[]>([]);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [paymentBusy, setPaymentBusy] = useState(false);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    accountApi<OrganizationDetail>(
      `/api/account/organizations/${encodeURIComponent(slug)}`,
    )
      .then(setData)
      .catch((cause) => {
        const status = (cause as { status?: number }).status;
        if (status === 401) {
          window.location.assign(
            `/kirjaudu?accountType=organization&next=${encodeURIComponent(window.location.pathname)}`,
          );
        } else if (status === 403) {
          window.location.assign("/organisaatioille");
        } else {
          setError(
            cause instanceof Error
              ? cause.message
              : "Organisaatiota ei voitu ladata.",
          );
        }
      });
  }, [slug, version]);

  useEffect(() => {
    accountApi<PublicMapResponse>("/api/public/areas")
      .then((result) => setAreas(result.areas ?? []))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!data) return;
    setProducts(
      data.availablePermitProducts.map((product) => ({
        id: product.id,
        name: product.name,
        areaName: null,
        provider: null,
        permitType: "fishing",
        validFrom: product.valid_from,
        validUntil: product.valid_to,
        priceAmount: null,
        currency: null,
        salesUrl: null,
        offerId: null,
        checkoutMode: "unavailable",
        sellerName: null,
        sourceCheckedAt: product.updated_at,
      })),
    );
  }, [data]);

  const setTab = (next: OrganizationTab) => {
    setTabState(next);
    const url = new URL(window.location.href);
    if (next === "overview") url.searchParams.delete("tab");
    else url.searchParams.set("tab", next);
    window.history.replaceState({}, "", url);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const finish = (value: string, nextTab?: OrganizationTab) => {
    setMessage(value);
    setError("");
    setVersion((current) => current + 1);
    if (nextTab) setTab(nextTab);
  };

  const fail = (cause: unknown, fallback: string) => {
    setMessage(cause instanceof Error ? cause.message : fallback);
  };

  return {
    areas,
    data,
    error,
    fail,
    finish,
    message,
    paymentBusy,
    products,
    selectedProduct,
    setError,
    setMessage,
    setPaymentBusy,
    setSelectedProduct,
    setTab,
    tab,
  };
}
